/* BB Fragrances · Mondame11 EDP PDP · built 12 August 2026 */
/* ══════════════════════════════════════════════════════════════════════
   BB FRAGRANCES · PDP behaviour
   One file for every bbf-* carousel: the product gallery, the review
   snippets on mobile, and the ritual steps on mobile.

   Load it once, ideally from theme.liquid before </body>:

       <script src="{{ 'bbf-pdp.js' | asset_url }}" defer></script>

   Every section that needs it also emits the tag itself, so a page is
   correct even if you skip that step — the browser fetches once.

   ── The wheel gesture ────────────────────────────────────────────────
   While the pointer is over a strip, ANY wheel gesture drives the
   carousel horizontally: a trackpad swipe (deltaX) or a plain mouse
   wheel (deltaY). One gesture advances exactly one frame. At the first
   or last frame the gesture is handed back to the page so vertical
   scrolling continues normally.

   Two things are deliberate and should not be "tidied":

   1. The wheel step sets `scrollLeft` directly rather than calling
      scrollTo({behavior:'smooth'}). Mandatory scroll-snap cancels an
      in-flight smooth scroll, which is what made an earlier version
      drop every second notch.
   2. A gesture is measured by ACCUMULATED delta (threshold 45) rather
      than per-event, because a trackpad fires dozens of small events
      for one physical swipe.

   A strip that is not actually scrollable (desktop, where the same
   markup lays out as a static grid) reports scrollWidth == clientWidth
   and the handler releases immediately — so binding is unconditional
   and the desktop layouts stay untouched.
   ══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var STEP_THRESHOLD = 45;   /* accumulated delta that counts as one gesture */
  var STEP_LOCK = 220;       /* ms before another step is allowed */
  var ACC_RESET = 220;       /* ms of stillness that ends a gesture */
  var IDLE_RESET = 700;      /* ms before the intended-frame guess is dropped */

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Index of the frame currently sitting at the start edge. */
  function nearest(track) {
    var kids = track.children;
    if (!kids.length) return 0;
    var base = kids[0].offsetLeft;
    var best = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < kids.length; i++) {
      var d = Math.abs(kids[i].offsetLeft - base - track.scrollLeft);
      if (d < bestDistance) { bestDistance = d; best = i; }
    }
    return best;
  }

  function offsetOf(track, i) {
    var child = track.children[i];
    if (!child || !track.children[0]) return 0;
    return child.offsetLeft - track.children[0].offsetLeft;
  }

  function Carousel(root) {
    /* The strip is usually a descendant, but a carousel with no dots and no
       caption (the review snippets) is just the strip itself. */
    var track = root.matches('[data-bbf-track]') ? root : root.querySelector('[data-bbf-track]');
    if (!track || !track.children.length) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-bbf-dot]'));
    var caption = root.querySelector('[data-bbf-caption]');
    var counter = root.querySelector('[data-bbf-count]');
    var prevBtn = root.querySelector('[data-bbf-prev]');
    var nextBtn = root.querySelector('[data-bbf-next]');
    var total = track.children.length;
    var index = -1;

    /* Arrows are driven by scroll POSITION rather than by the frame index,
       because a peek carousel shows part of the next frame — the last
       frame can be reached without its index ever becoming the nearest
       one. Position is the only thing that knows when there is no more
       to see. */
    function updateArrows() {
      if (!prevBtn && !nextBtn) return;
      var max = track.scrollWidth - track.clientWidth;
      var at = track.scrollLeft;
      var atStart = at <= 1;
      var atEnd = at >= max - 1;
      if (prevBtn) {
        prevBtn.disabled = atStart;
        prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.disabled = atEnd;
        nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
    }

    /* ── paint the frame-dependent chrome ──────────────────────────── */
    function paint(i) {
      if (i === index || i < 0 || i >= total) return;
      index = i;
      for (var n = 0; n < dots.length; n++) {
        var on = n === i;
        dots[n].classList.toggle('is-on', on);
        dots[n].setAttribute('aria-current', on ? 'true' : 'false');
      }
      var slide = track.children[i];
      if (caption && slide) caption.textContent = slide.getAttribute('data-bbf-caption-text') || '';
      if (counter) counter.textContent = (i + 1) + ' / ' + total;
      updateArrows();
    }

    /* ── dots and any other explicit jump ──────────────────────────── */
    function goTo(i, smooth) {
      if (i < 0 || i >= total) return;
      track.scrollTo({
        left: offsetOf(track, i),
        behavior: smooth && !reduceMotion() ? 'smooth' : 'auto'
      });
      paint(i);
    }

    for (var d = 0; d < dots.length; d++) {
      (function (button, i) {
        button.addEventListener('click', function () { goTo(i, true); });
      })(dots[d], d);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(nearest(track) - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(nearest(track) + 1, true); });

    /* ── keep the chrome honest during a native touch swipe ────────── */
    var rafPending = false;
    track.addEventListener('scroll', function () {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(function () {
        rafPending = false;
        paint(nearest(track));
        updateArrows();
      });
    }, { passive: true });

    /* ── the wheel gesture ─────────────────────────────────────────── */
    var acc = 0;
    var lockUntil = 0;
    var accTimer = null;
    var intended = null;
    var idleTimer = null;

    track.addEventListener('wheel', function (e) {
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 1) return;                    /* not scrollable — desktop grid */

      var horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      var delta = horizontal ? e.deltaX : e.deltaY;
      if (e.deltaMode === 1) delta *= 16;            /* lines  */
      else if (e.deltaMode === 2) delta *= track.clientWidth;  /* pages */
      if (!delta) return;

      var at = track.scrollLeft;
      if ((delta < 0 && at <= 1) || (delta > 0 && at >= max - 1)) return; /* release to the page */

      e.preventDefault();

      var now = Date.now();
      if (now < lockUntil) return;                   /* one gesture, one frame */

      if (acc && (acc > 0) !== (delta > 0)) acc = 0; /* direction flipped */
      acc += delta;

      window.clearTimeout(accTimer);
      accTimer = window.setTimeout(function () { acc = 0; }, ACC_RESET);

      if (Math.abs(acc) < STEP_THRESHOLD) return;

      /* Track the frame we are heading for, so a fast gesture keeps
         stepping while the previous step is still settling. */
      var from = intended === null ? nearest(track) : intended;
      var next = from + (acc > 0 ? 1 : -1);
      acc = 0;
      lockUntil = now + STEP_LOCK;
      if (next < 0 || next >= total) return;

      intended = next;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(function () { intended = null; }, IDLE_RESET);

      track.scrollLeft = offsetOf(track, next);
      paint(next);
    }, { passive: false });

    /* ── click and drag ────────────────────────────────────────────
       Mouse and pen only: touch already scrolls the strip natively, and
       hijacking it would be worse than the default. Snap is switched off
       for the duration of the drag (see .is-dragging) because otherwise
       every pointermove is fought by the snap engine; on release the
       strip settles onto whichever frame is nearest.

       A drag that moved more than a few pixels swallows the click that
       follows it, so dragging across a dot or the wishlist heart does not
       also press it. */
    var dragging = false, dragStartX = 0, dragStartScroll = 0, dragMoved = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      if (track.scrollWidth - track.clientWidth <= 1) return;
      dragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      try { track.setPointerCapture(e.pointerId); } catch (err) {}
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 3) dragMoved = true;
      track.scrollLeft = dragStartScroll - dx;
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      try { track.releasePointerCapture(e.pointerId); } catch (err) {}
      goTo(nearest(track), true);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    track.addEventListener('click', function (e) {
      if (!dragMoved) return;
      e.preventDefault();
      e.stopPropagation();
      dragMoved = false;
    }, true);

    /* ── keyboard, for the dots' sake ──────────────────────────────── */
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { goTo(nearest(track) + 1, true); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { goTo(nearest(track) - 1, true); e.preventDefault(); }
    });

    paint(nearest(track));
    updateArrows();

    /* A carousel that only exists below a breakpoint has to re-check its
       arrows when the layout changes under it. */
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { updateArrows(); }).observe(track);
    } else {
      window.addEventListener('resize', updateArrows);
    }
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-bbf-carousel]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__bbfBound) continue;
      roots[i].__bbfBound = true;
      Carousel(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor: sections are re-rendered without a page load. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
